import type { TastingNoteSummary } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { createWineNote, deleteWineNote, updateWineNote } from "../api.js";
import { useCellar } from "../cellar.js";
import { actionButton, control, fieldLabel, resting } from "./controls.js";

/**
 * `10.25` — what this wine was like when it was drunk somewhere else.
 *
 * The decision of 2026-09-26 made the wine the owner of a note and the bottle
 * the case where there was one. The notes with a bottle are where they always
 * were, under the bottle in *Druckna flaskor*; these are the others — a
 * restaurant, a friend's table, a Munskänkarna evening — which is the tasting
 * that most often decides whether the bottles here are opened now or kept.
 *
 * **Always drawn**, heading and all, unlike the departed bottles above it. The
 * section is how a note gets written, and a wine nobody has written about yet
 * is exactly the one the button is for; the sentence under the heading is
 * what keeps a section with nothing in it from reading as a failure to load.
 *
 * **Anybody who can read the cellar may write here, and only ever edit their
 * own** — the owner and the guests, the decision's answer to who. The server
 * decides whose a note is, and the page reads `mine` rather than comparing
 * names, because two people can share one.
 */
export function TastedElsewhere({
  cellarId,
  wineId,
  notes,
  onChanged,
}: {
  cellarId: string;
  wineId: string;
  /** Every note on the wine; the ones without a bottle are this section's. */
  notes: readonly TastingNoteSummary[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [writing, setWriting] = useState<"new" | string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeFailed, setRemoveFailed] = useState(false);
  const elsewhere = notes.filter((note) => note.bottleId === null);

  const remove = (note: TastingNoteSummary) => {
    setRemoveFailed(false);
    void deleteWineNote(cellarId, wineId, note.id)
      .then(() => {
        setRemoving(null);
        onChanged();
      })
      .catch(() => setRemoveFailed(true));
  };

  return (
    <section>
      <h2 className="font-display border-line mt-10 border-b pb-2 text-lg">
        {t("wine.elsewhere.heading")}
      </h2>
      {elsewhere.length === 0 && (
        <p className="text-muted mt-3 max-w-prose text-sm">{t("wine.elsewhere.intro")}</p>
      )}

      <ul>
        {elsewhere.map((note) => (
          <li key={note.id} className="border-line border-b py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span>
                {note.tastedOn}
                {note.place !== null && (
                  <span className="text-muted"> · {note.place}</span>
                )}
              </span>
              {note.mine && writing !== note.id && (
                <span className="flex items-baseline gap-4 text-sm">
                  <button
                    type="button"
                    className="text-accent underline-offset-4 hover:underline"
                    onClick={() => setWriting(note.id)}
                  >
                    {t("wine.elsewhere.edit")}
                  </button>
                  <button
                    type="button"
                    className="text-muted hover:text-ink underline-offset-4 hover:underline"
                    onClick={() => setRemoving(removing === note.id ? null : note.id)}
                  >
                    {t("wine.elsewhere.remove")}
                  </button>
                </span>
              )}
            </div>

            {/* The same line a bottle's note is printed as, so the two kinds of
                note read as one habit: faulty first, the score in the display
                face, the words, and a name on everybody's but the reader's. */}
            {writing !== note.id && (
              <p className="text-muted mt-1 max-w-prose text-sm">
                {note.isFaulty && (
                  <span className="text-past">{t("wine.faulty")} · </span>
                )}
                {note.score !== null && (
                  <span className="font-display text-ink">{note.score} </span>
                )}
                {note.text}
                {!note.mine && (
                  <span className="text-muted">
                    {" — "}
                    {note.authorName ?? t("wine.note.someoneGone")}
                  </span>
                )}
              </p>
            )}

            {/* Removing is asked once, in place — not a browser dialog, and not
                on the first tap: a note is somebody's evening and there is no
                undo for it. */}
            {removing === note.id && (
              <p className="mt-2 flex flex-wrap items-baseline gap-4 text-sm">
                <span>{t("wine.elsewhere.removeAsk")}</span>
                <button
                  type="button"
                  className="text-past underline-offset-4 hover:underline"
                  onClick={() => remove(note)}
                >
                  {t("wine.elsewhere.removeYes")}
                </button>
                <button
                  type="button"
                  className="text-muted hover:text-ink underline-offset-4 hover:underline"
                  onClick={() => setRemoving(null)}
                >
                  {t("wine.cancel")}
                </button>
                {removeFailed && (
                  <span role="alert" className="text-past">
                    {t("wine.elsewhere.removeFailed")}
                  </span>
                )}
              </p>
            )}

            {writing === note.id && (
              <WineNoteForm
                cellarId={cellarId}
                wineId={wineId}
                note={note}
                onDone={() => {
                  setWriting(null);
                  onChanged();
                }}
                onCancel={() => setWriting(null)}
              />
            )}
          </li>
        ))}
      </ul>

      {writing === "new" ? (
        <WineNoteForm
          cellarId={cellarId}
          wineId={wineId}
          note={null}
          onDone={() => {
            setWriting(null);
            onChanged();
          }}
          onCancel={() => setWriting(null)}
        />
      ) : (
        <button
          type="button"
          className="text-accent mt-3 text-sm underline-offset-4 hover:underline"
          onClick={() => setWriting("new")}
        >
          {t("wine.elsewhere.add")}
        </button>
      )}
    </section>
  );
}

/**
 * Writing or correcting one note. The bottle note's form with one more field,
 * *where*, and the date starting at today rather than at a departure — there
 * is no departure; the evening is whenever somebody says it was.
 */
function WineNoteForm({
  cellarId,
  wineId,
  note,
  onDone,
  onCancel,
}: {
  cellarId: string;
  wineId: string;
  note: TastingNoteSummary | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const scale = useCellar()?.tastingScale ?? null;
  const id = note?.id ?? "new";
  const [tastedOn, setTastedOn] = useState(
    note?.tastedOn ?? new Date().toISOString().slice(0, 10),
  );
  const [place, setPlace] = useState(note?.place ?? "");
  const [text, setText] = useState(note?.text ?? "");
  const [score, setScore] = useState(
    note === null || note.score === null ? "" : String(note.score),
  );
  const [isFaulty, setIsFaulty] = useState(note?.isFaulty ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const given = score.trim() !== "";
  const value = Number(score.replace(",", "."));
  const sound =
    !given ||
    (scale !== null &&
      Number.isFinite(value) &&
      value >= 0 &&
      value <= scale.max &&
      (scale.hasDecimals || Number.isInteger(value)));
  // A note says something: words, a score, or that the bottle was faulty. The
  // route refuses one that says nothing, and the button says so first.
  const saysSomething = text.trim() !== "" || given || isFaulty;
  const canSubmit = tastedOn !== "" && sound && saysSomething && !busy;

  return (
    <form
      // The score's column is wide enough for *Mitt betyg (av 20)* on one
      // line; at six rems its label broke in two above a two-digit field.
      className="mt-3 grid gap-3 sm:grid-cols-[10rem_1fr_9rem]"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        const values = {
          tastedOn,
          place: place.trim() === "" ? null : place,
          text,
          score: given ? value : null,
          isFaulty,
        };
        void (
          note === null
            ? createWineNote(cellarId, wineId, values)
            : updateWineNote(cellarId, wineId, note.id, values)
        )
          .then(onDone)
          .catch(() => setError(t("wine.note.saveFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div>
        <label className={fieldLabel} htmlFor={`elsewhere-date-${id}`}>
          {t("wine.drunkOn")}
        </label>
        <input
          id={`elsewhere-date-${id}`}
          type="date"
          value={tastedOn}
          onChange={(event) => setTastedOn(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor={`elsewhere-place-${id}`}>
          {t("wine.elsewhere.place")}
        </label>
        <input
          id={`elsewhere-place-${id}`}
          value={place}
          placeholder={t("wine.elsewhere.placeHint")}
          onChange={(event) => setPlace(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor={`elsewhere-score-${id}`}>
          {scale === null
            ? t("wine.note.score")
            : t("wine.ownRating", { max: scale.max })}
        </label>
        <input
          id={`elsewhere-score-${id}`}
          inputMode={scale?.hasDecimals === true ? "decimal" : "numeric"}
          value={score}
          aria-invalid={!sound}
          onChange={(event) => setScore(event.target.value)}
          className={`${control} ${resting} font-display mt-1 text-center`}
        />
      </div>

      <div className="col-span-full">
        <label className={fieldLabel} htmlFor={`elsewhere-text-${id}`}>
          {t("wine.tastingNote")}
        </label>
        <textarea
          id={`elsewhere-text-${id}`}
          value={text}
          rows={3}
          onChange={(event) => setText(event.target.value)}
          className={`${control} ${resting} mt-1 h-auto py-2`}
        />
      </div>

      <label className="col-span-full flex items-baseline gap-2 text-sm">
        <input
          type="checkbox"
          checked={isFaulty}
          onChange={(event) => setIsFaulty(event.target.checked)}
        />
        {t("wine.faulty")}
      </label>

      <div className="col-span-full flex flex-wrap items-center gap-4">
        <button type="submit" disabled={!canSubmit} className={actionButton}>
          {busy ? t("wine.saving") : t("wine.note.save")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted hover:text-ink text-sm underline-offset-4 hover:underline"
        >
          {t("wine.cancel")}
        </button>
        {!saysSomething && (
          <span className="text-muted text-sm">{t("wine.elsewhere.empty")}</span>
        )}
        {error !== null && (
          <span role="alert" className="text-past text-sm">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}
